import React, { useEffect } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import SubpageHero from "@/components/SubpageHero";
import SubpageLayout from "@/components/SubpageLayout";
import SectionNumber from "@/components/SectionNumber";
import Embers from "@/components/Embers";
import { SITE } from "@/lib/data";
import useScrollReveal from "@/hooks/useScrollReveal";

const VALUES = [
  {
    n: "01",
    t: "Tűz",
    d: "Nem főzési technika — hangulat, közösség és történet.",
  },
  {
    n: "02",
    t: "Lassúság",
    d: "Az ízek és a beszélgetések egyaránt időt kérnek.",
  },
  {
    n: "03",
    t: "Természet",
    d: "Tisza-part, fa, állatok, csillagos ég.",
  },
  {
    n: "04",
    t: "Közösség",
    d: "Hosszú asztal, közös tűz, megosztott élmény.",
  },
];

const ANIMALS = [
  { n: "Di & Dra", role: "Alpakák", img: "/images/zhbsgoo7.jpeg" },
  { n: "Mang", role: "Póni", img: "/images/nub9tov3.jpg" },
  { n: "Tequila & Fiona", role: "Lovak", img: "/images/nub9tov3.jpg" },
  { n: "Kutyacsapat", role: "Házőrzők", img: "/images/wdrhfyom.jpeg" },
];

export default function Rolunk() {
  useScrollReveal();
  useEffect(() => {
    document.title = "Rólunk · Pakkaland";
  }, []);

  return (
    <SubpageLayout testId="page-rolunk">
      <SubpageHero
        crumb="Rólunk"
        eyebrow="A történetünk"
        title="Rólunk"
        tagline="A Pakkaland egy vidéki élménybirtok Vezsenyen, ahol a tűz nem csak főzési technika, hanem hangulat, közösség és történet."
        image="/images/jd0m09ac.jpg"
      />

      {/* Story */}
      <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="01" />
        <Embers count={8} variant="light" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 relative grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 reveal">
            <span className="eyebrow">Történet</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-[3.5rem] mt-6 leading-[1.05]">
              Hogyan<br />
              <span className="italic text-pkred">kezdődött?</span>
            </h2>
          </div>
          <div className="lg:col-span-8 lg:pl-8 lg:border-l border-pkash reveal">
            <p className="font-serif text-xl sm:text-2xl text-pkcharcoal leading-[1.45] font-light">
              Egy Tisza-parti porta. Egy kemence. Egy ötlet: a tűz mellett az
              idő lelassul, az ízek elmélyülnek, az emberek közelebb kerülnek
              egymáshoz.
            </p>
            <p className="mt-6 text-pkcharcoal/70 text-base sm:text-lg leading-relaxed">
              Vezseny csendes falujában építettük fel a Pakkalandot — egy
              olyan helyet, ahol a falusi vendégasztal, a kemencés estek, a
              vendégház és a kézműves műhely egyetlen élményben kapcsolódik
              össze. Itt mindenki a tűz mellett ül — szakácsok, házigazdák,
              vendégek, gyerekek és állatok egyaránt.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <span className="block w-8 h-px bg-pkred" />
              <span className="text-[0.65rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
                Vezsenyen · 2014 óta
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative bg-pkbeige py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="02" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <span className="eyebrow center">Értékeink</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Négy szó<span className="text-pkred">,</span>
              <br />
              <span className="italic text-pkred">amiben hiszünk.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-pkash">
            {VALUES.map((v, i) => (
              <div
                key={v.t}
                className="bg-pkbeige p-8 sm:p-10 reveal"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="text-pkred font-serif text-5xl font-extralight">
                  {v.n}
                </div>
                <h3 className="font-serif text-2xl text-pkcharcoal mt-4">
                  {v.t}
                </h3>
                <p className="text-pkcharcoal/65 text-sm mt-3 leading-relaxed">
                  {v.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Animals / family */}
      <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="03" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="max-w-2xl mb-14 reveal">
            <span className="eyebrow">A család</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              A kertünk<br />
              <span className="italic text-pkred">lakói.</span>
            </h2>
            <p className="mt-6 text-pkcharcoal/70 text-base sm:text-lg leading-relaxed max-w-xl">
              Pakkaland nem csak helyszín — barátságos állatok, az alpakkák,
              a póni, a lovak és a kutyacsapat is részei a mindennapoknak.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {ANIMALS.map((a, i) => (
              <div
                key={a.n}
                data-testid={`team-animal-${i}`}
                className="group relative aspect-[3/4] overflow-hidden bg-pkcharcoal reveal"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img
                  src={a.img}
                  alt={a.n}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal/85 via-pkcharcoal/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="w-8 h-px bg-pkred mb-3 group-hover:w-16 transition-all duration-500" />
                  <div className="font-serif text-white text-xl sm:text-2xl">
                    {a.n}
                  </div>
                  <div className="text-[0.6rem] tracking-[0.25em] uppercase text-white/65 mt-1">
                    {a.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="relative bg-pkcharcoal py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="04" variant="dark" />
        <div
          className="absolute inset-0 pointer-events-none opacity-35"
          style={{
            background:
              "radial-gradient(ellipse at 50% 100%, rgba(217,4,41,0.45) 0%, transparent 60%)",
          }}
        />
        <Embers count={16} />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <span className="eyebrow center">Találkozzunk</span>
          <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            Gyere el<span className="text-pkred">,</span>
            <br />
            <span className="italic text-pkred">ülj le a tűz mellé.</span>
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={SITE.phoneHref}
              data-testid="rolunk-cta-phone"
              className="btn-fire"
            >
              <Phone className="w-4 h-4" strokeWidth={1.8} />
              {SITE.phone}
            </a>
            <a
              href={SITE.emailHref}
              data-testid="rolunk-cta-email"
              className="btn-ghost"
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              Email
            </a>
          </div>
          <div className="mt-10 flex items-center justify-center gap-2 text-white/65 text-sm">
            <MapPin className="w-4 h-4 text-pkred" strokeWidth={1.6} />
            {SITE.address}
          </div>
        </div>
      </section>
    </SubpageLayout>
  );
}
